import { UseFormReturn } from "react-hook-form";

import Form from "../base/Form/Form";
import { Button } from "../base/Button";
import { FormToggle } from "../base/Form/FormToggle";
import { FormTextField } from "../base/Form/FormTextField";
import Modal, { ModalControlProps } from "../../layouts/Modal";
import {
  CreateUpdateUserFormFields,
  UserClaims,
} from "./../../models/User/CreateUpdateUser";
import useCustomerUsersMutations from "../../data/CustomerUser/useCustomerUsersMutations";
import { FormErrors } from "../base/Form/FormErrors";
import { IUser } from "../../models/User/User";

interface CreateUpdateUserModalProps extends ModalControlProps {
  customerId: string;
  user?: IUser;
  onChangePassword?: () => void;
}

export function CreateUpdateUserModal({
  open,
  setOpen,
  customerId,
  user,
  onChangePassword,
}: CreateUpdateUserModalProps) {
  const { create, update } = useCustomerUsersMutations(customerId);

  const createUser = (data: CreateUpdateUserFormFields) => {
    const claims = Object.keys(data.claims).reduce<Record<string, string>>(
      (acc, key) => {
        acc[key] = data.claims[key] ? "true" : "false";
        return acc;
      },
      {}
    );

    create.mutate(
      {
        ...data,
        customerId,
        email: data.email?.trim() || null,
        claims: claims as UserClaims
      },
      {
        onSuccess: () => {
          setOpen(false);
        },
      }
    );
  };

  const updateUser = (data: CreateUpdateUserFormFields) => {
    const claims = Object.keys(data.claims).reduce<Record<string, string>>(
      (acc, key) => {
        acc[key] = data.claims[key] ? "true" : "false";
        return acc;
      },
      {}
    );

    update.mutate(
      {
        ...data,
        customerId,
        id: user?.id,
        email: data.email?.trim() || null,
        claims: claims as UserClaims,
      },
      {
        onSuccess: () => {
          setOpen(false);
        },
      }
    );
  };

  return (
    <Modal open={open} setOpen={setOpen} width="!w-1/3">
      <div>
        <div className="border-b">
          <h2 className="text-lg font-stick font-medium leading-6 mb-4 text-gray-900">
            {user ? "Edit User" : "Add User"}
          </h2>
        </div>
        <Form<CreateUpdateUserFormFields>
          defaultValues={
            user
              ? {
                ...user,
                claims: user.userClaims.reduce((acc, claim) => {
                  acc[claim.type] = claim.value === "true" ? true : false;
                  return acc;
                }, {} as any),
              }
              : {}
          }
          onSubmit={user ? updateUser : createUser}
          className="pt-4 grid grid-cols-1 gap-y-4 md:grid-cols-2 md:gap-4"
        >
          {({
            register,
            control,
            setValue,
          }: UseFormReturn<CreateUpdateUserFormFields>) => (
            <>
              <div className="-m-4 md:col-span-2">
                <FormErrors />
              </div>
              <FormTextField
                {...register("forename", { required: true })}
                label="Forename"
              />
              <FormTextField
                {...register("surname", { required: true })}
                label="Surname"
              />
              <div className="md:col-span-2">
                <FormTextField
                  {...register("email", {
                    onChange: (e) =>
                      setValue("username", e.currentTarget.value),
                  })}
                  label="Email Address"
                />
              </div>
              <FormTextField
                {...register("username", { required: true })}
                label="Username"
              />
              {!user && (
                <FormTextField
                  {...register("password", { required: true })}
                  type="password"
                  label="Password"
                />
              )}
              <div className="my-4 md:col-span-2">
                <FormToggle
                  {...register("ssoLogin")}
                  control={control}
                  toggleProps={{ label: "SSO Login" }}
                />
                <p className="text-xs text-gray-500 mt-1">
                  When enabled, this user will be automatically redirected to Microsoft login.
                </p>
              </div>
              <div className="my-4 md:col-span-2 grid grid-cols-1 gap-y-4 md:grid-cols-2 md:gap-4">
                <FormToggle
                  {...register("claims.isAdmin")}
                  control={control}
                  toggleProps={{ label: "Admin" }}
                />
                <FormToggle
                  {...register("claims.isStaff")}
                  control={control}
                  toggleProps={{ label: "Staff" }}
                />
                <FormToggle
                  {...register("claims.isDriver")}
                  control={control}
                  toggleProps={{ label: "Driver" }}
                />
                <FormToggle
                  {...register("claims.isMechanic")}
                  control={control}
                  toggleProps={{ label: "Mechanic" }}
                />
                <FormToggle
                  {...register("claims.isExporter")}
                  control={control}
                  toggleProps={{ label: "Exporter" }}
                />
              </div>
              <Button type="submit">Save</Button>
              <Button type="reset" variant="outlined">
                Reset
              </Button>
              {user && onChangePassword && (
                <button
                  type="button"
                  className="md:col-span-2 text-sm text-gray-500 hover:text-gray-700"
                  onClick={onChangePassword}
                >
                  Change password
                </button>
              )}
            </>
          )}
        </Form>
      </div>
    </Modal>
  );
}
